import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatDate } from "@repo/utils/date";
import type React from "react";

import { useGetPublicTermsDataQuery } from "@/libs/generated/hooks";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "box-shadow 0.3s ease-in-out",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -4,
    left: 0,
    width: "10%",
    height: "2px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: "1px",
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

/**
 * Parse content field which can be string, array, or object
 */
function parseContent(content: any): string[] {
  if (!content) return [];

  if (typeof content === "string") {
    return [content];
  }

  if (Array.isArray(content)) {
    return content.filter((item) => typeof item === "string");
  }

  if (typeof content === "object") {
    // Handle object content - could be JSON with text fields
    if (content.text) return Array.isArray(content.text) ? content.text : [content.text];
    if (content.paragraphs) return content.paragraphs;
    if (content.items) return content.items;
  }

  return [];
}

/**
 * Enhanced subsection parsing with support for nested structures and types
 */
function parseSubsections(subsections: any[] | null | undefined): Array<{
  id: string;
  content: string;
  type?: "bullet" | "numbered" | "highlight";
}> {
  if (!subsections || !Array.isArray(subsections)) return [];

  return subsections
    .map((subsection, index) => {
      if (typeof subsection === "string") {
        return {
          id: `subsection-${index}`,
          content: subsection,
          type: "bullet" as const,
        };
      }

      if (subsection?.text) {
        return {
          id: `subsection-${index}`,
          content: subsection.text,
          type: subsection.type || ("bullet" as const),
        };
      }

      if (subsection?.content) {
        return {
          id: `subsection-${index}`,
          content: subsection.content,
          type: subsection.type || ("bullet" as const),
        };
      }

      return null;
    })
    .filter(Boolean) as Array<{
    id: string;
    content: string;
    type?: "bullet" | "numbered" | "highlight";
  }>;
}

/**
 * Loading skeleton for terms sections
 */
function TermsLoadingSkeleton() {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mx: "auto", mb: 2 }} />
        <Skeleton variant="rectangular" width={200} height={32} sx={{ mx: "auto" }} />
      </Box>

      <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 2 }} />

      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={24} />
        </Paper>
      ))}
    </Container>
  );
}

/**
 * Terms and Conditions component with modern design and responsive layout
 */
function Terms() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data, isPending, error } = useGetPublicTermsDataQuery();

  // Early returns for loading and error states
  if (isPending) {
    return <TermsLoadingSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load terms and conditions. Please try again later.
        </Alert>
      </Container>
    );
  }

  if (!data?.getPublicTermsData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 4 }}>
          Terms and conditions are currently unavailable.
        </Alert>
      </Container>
    );
  }

  const termsData = data.getPublicTermsData;
  const displayTitle = termsData.title || "Terms and Conditions";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {displayTitle}
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
          {termsData.lastUpdated && (
            <Chip
              label={`Last updated: ${formatDate(termsData.lastUpdated)}`}
              variant="outlined"
              color="primary"
              sx={{ mt: 1 }}
            />
          )}
          {termsData.version && (
            <Chip
              label={`Version: ${termsData.version}`}
              variant="outlined"
              color="secondary"
              sx={{ mt: 1 }}
            />
          )}
        </Stack>
      </Box>

      {termsData.sections?.map((section, index) => {
        const contentItems = parseContent(section.content);
        const parsedSubsections = parseSubsections(section.subsections);

        return (
          <StyledPaper key={section.id} elevation={0}>
            <SectionHeader variant="h5">
              {index + 1}.{` ${section.title}`}
            </SectionHeader>

            {contentItems.length > 0 && (
              <List disablePadding>
                {contentItems.map((item, itemIndex) => (
                  <StyledListItem key={itemIndex} disableGutters>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "body1",
                        sx: { lineHeight: 1.7, color: "text.primary" },
                      }}
                    />
                  </StyledListItem>
                ))}
              </List>
            )}

            <EnhancedSubsections subsections={parsedSubsections} />
          </StyledPaper>
        );
      })}
    </Container>
  );
}

export default Terms;

const StyledSubsection = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2, 0, 2, 2),
  borderLeft: `4px solid ${theme.palette.primary.light}`,
}));

const SubsectionBullet = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const SubsectionText = styled(Typography)(({ theme }) => ({
  flex: 1,
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  "&:hover": {
    color: theme.palette.text.primary,
  },
}));

const EnhancedSubsections: React.FC<{
  subsections: Array<{
    id: string;
    content: string;
    type?: "bullet" | "numbered" | "highlight";
  }>;
}> = ({ subsections }) => {
  if (subsections.length === 0) return null;

  return (
    <>
      <Divider sx={{ my: 3, opacity: 0.4 }} />
      <Box sx={{ mt: 2 }}>
        {subsections.map((subsection) => (
          <StyledSubsection key={subsection.id}>
            <SubsectionBullet>
              <SubsectionText variant="body2">{subsection.content}</SubsectionText>
            </SubsectionBullet>
          </StyledSubsection>
        ))}
      </Box>
    </>
  );
};
